<script setup>
import {computed, ref} from "vue";
import {emitter} from "@/emitter";
import {formatTime, getClipDuration, timeFromTs} from "@/helpers";
import TransitionModal from "@/components/live-editor/TransitionModal.vue";
import store from "@/vuex/store";
import ClipModal from "@/components/live-editor/ClipModal.vue";
import ConfirmModal from "@/components/common/ConfirmModal.vue";
import {getClipsDuration} from "@/helpers";

const showConfirmClearModal = ref(false)
const showConfirmDeleteModal = ref(false)

const clips = computed(() => store.getters["live/getClips"])
const duration = computed(() => store.getters["live/getClipsDuration"])
const removedIndex = ref(null)

const removeClip = (index) => {
  store.commit('live/removeClip', index)
  showConfirmDeleteModal.value = false
};

function clearClips() {
  store.commit('live/clearClips')
  showConfirmClearModal.value = false
}

function openTransitionModal(index) {
  emitter.emit('showTransitionModal', index);
}

function openClipModal(clip) {
  emitter.emit('showClipModal', clip)
}
</script>

<template>
  <section class="mx-auto pt-1">
    <!--    <div class="p-8 rounded-[4px] bg-white border border-[!F3F4F5]">-->
    <div class=" bg-white border-t border-b ">
      <div class="flex justify-between items-center mb-4 mt-2">
        <div class="flex gap-2 items-center">
          <h2 class="text-[18px] font-bold text-dark_blue">Clips </h2>
          <div v-if="clips.length"
               class="text-xs font-mono bg-indigo-500/10 text-indigo-300 px-2 py-1 rounded">
            {{ duration.toFixed(1) }}s
          </div>
        </div>
        <div class="flex gap-4 items-center">
          <button
            v-if="clips.length"
            @click="showConfirmClearModal = true"
            class="flex gap-2 items-center relative px-7 py-3 text-xs font-bold text-white rounded-[4px] cursor-pointer bg-orange-450 hover:bg-orange-450/80 transition"
          >
            Clear
          </button>
          <button
            v-if="clips.length"
            @click="openTransitionModal(null)"
            class="flex gap-2 items-center relative px-7 py-3 text-xs font-bold text-white rounded-[4px] cursor-pointer bg-blue-450 hover:bg-blue-450/80 transition"
          >
            Bulk Transitions
          </button>
        </div>
      </div>

      <div class="flex items-center gap-3 overflow-x-auto pb-2 relative">

        <div v-if="!clips.length"
             class="w-full flex flex-col items-center justify-center py-12 text-slate-600 border-[1px] border-dashed border-gray-400 rounded-[4px] bg-gray-200">
          <i class="fas fa-film text-4xl mb-4 opacity-20"></i>
          <p class="text-sm text-center px-2">Select a range on the timeline to create your first clip.</p>
        </div>

        <template v-for="(clip, index) in clips" :key="clip.id">
          <!-- CLIP CARD -->
          <div class="clip-card group relative bg-gray-300" @click="clip.video_url ? openClipModal(clip): ''">
            <transition v-if="clip.loading" name="fade">
              <div
                class="absolute inset-0 bg-white/60 backdrop-blur-md flex items-center justify-center z-[10] p-4"
              >
                <div class="text-center text-blue-450">
                  <p><i class="fa fa-spinner text-[24px] animate-spin"></i></p>
                  <p class="mt-2 text-[14px]"> Preparing...</p>
                </div>

              </div>
            </transition>
            <img :src="clip.thumbnail"
                 class="w-full h-32 object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                 draggable="false" alt="clip">

            <div v-if="clip.failed" class="absolute top-0 lef-0 w-full h-full flex items-center justify-center bg-gray-500/70">
              <div class="flex gap-2 flex-col text-center text-orange-450 mb-10">
                <i class="fa fa-triangle-exclamation  text-[32px]"></i>
                <p class="text-[12px] font-bold bg-black/60 text-white px-2 py-0.5 rounded-[4px]">Error while preparing clip</p>
              </div>
            </div>
            <!-- Overlay -->
            <div
              class="absolute inset-0 clip-overlay flex flex-col justify-end p-4 opacity-90 group-hover:opacity-100 transition-opacity">
              <div class="flex justify-between items-end h-full">
                <div class="flex gap-2 flex-col justify-end h-full">
                  <div class="flex ">
                    <p v-if="clip.duration && !clip.failed"
                       class="bg-blue-450 rounded-[4px] px-2 py-0.5 text-white text-[10px] font-bold">
                      {{ getClipDuration(clip)?.toFixed(2) }} s</p>
                  </div>
                  <div v-if="clip.trim"
                       class="flex flex-col gap-1 text-white font-mono leading-relaxed text-[11px] font-bold">
                    <p class="bg-black/70 rounded-[4px] px-2 py-0.5">
                      {{ timeFromTs((clip.trim?.start || 0), true, true) }}</p>
                    <p class="bg-black/70 rounded-[4px] px-2 py-0.5">
                      {{ timeFromTs((clip.trim?.end || clip.duration * 1000), true, true) }}</p>
                  </div>
                </div>
                <button @click.stop="removedIndex = index; showConfirmDeleteModal = true"
                        class="w-8 h-8 rounded-full bg-gray-200 text-gray-500 hover:bg-rose-500 hover:text-white flex items-center justify-center transition-all transform hover:scale-110 backdrop-blur-sm">
                  <i class="fas fa-trash-alt text-xs"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- TRANSITION NODE -->
          <div v-if="index < clips.length - 1" class="flex flex-col items-center gap-1 group ">
            <p
              class="absolute top-0 bg-orange-450 px-2 py-1 text-white text-[12px] rounded-[4px] min-w-[145px] text-center z-[20] cursor-pointer"
              @click="openTransitionModal(clip.id)"
              v-if="clip.transition">{{ clip.transition.label }}</p>

            <el-tooltip :content="clip.transition ? 'Change Transition' : 'Add transition'" popper-class="blue-tooltip"
                        :enterable="false" :hide-after="0">
              <button
                @click="openTransitionModal(clip.id)"
                class="w-10 h-10 rounded-full bg-blue-450 text-white"
              >
                <i v-if="!clip.transition" class="fas fa-plus"></i>
                <i v-else class="fa fa-refresh"></i>
              </button>
            </el-tooltip>
          </div>
        </template>
      </div>
    </div>
  </section>
  <TransitionModal/>
  <ClipModal/>
  <ConfirmModal :isOpen="showConfirmClearModal"
                content="Are you sure you want to delete all clips?"
                @close="showConfirmClearModal = false"
                @cancel="showConfirmClearModal = false"
                @confirm="clearClips"/>

  <ConfirmModal :isOpen="showConfirmDeleteModal"
                content="Are you sure you want to delete this clip?"
                @close="showConfirmDeleteModal = false; removedIndex = null"
                @cancel="showConfirmDeleteModal = false; removedIndex = null"
                @confirm="removeClip(removedIndex)"/>
</template>
